import {
    Box,
    Button,
    Checkbox,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    FileDownloadOutlined,
    KeyboardArrowDown,
    KeyboardArrowUp,
    MoreVert,
    SearchOutlined,
    ViewWeekOutlined,
} from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import {
    Fragment,
    MouseEvent,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PALO_MAX_RESULTS, PaloTableFoot } from './palo-table-foot';
import { PaloColumnType } from './palo-column-type';
import { PaloColumnVariant, columnVariantSx } from './palo-column-variant';
import { PaloOverflowAction, PaloOverflowMenu } from './palo-overflow-menu';
import { PaloQuickFilter, PaloQuickFilterCell } from './palo-quick-filter';
import { PaloRowActionButton, paloSoleIconAction } from './palo-row-action-button';
import { paloColumnIsFlexible, paloColumnLayout } from './palo-column-layout';
import { paloCurrencySign } from './palo-amount';
import { paloFilterPart, usePaloRefusedFilter } from './palo-list-refusal';
import { paloLinkProps, paloLinkReset, paloOpenInNewTab, paloWantsNewTab } from './palo-link';
import { rememberPaloListOrder } from './palo-record-pager';
import { usePaloHeadSlot } from './palo-detail-view';
import { usePaloShortcut } from './palo-shortcuts';

/** The column as it is drawn: what the list says, filled in by its kind. */
/**
 * The whole text of a cell that did not fit, as the browser's own tooltip.
 *
 * A list cuts a long value with "…" and the rest was simply gone (LST-S11).
 * Measured when the pointer arrives rather than on every render: a page of
 * 50 rows draws several hundred cells, and almost none of them is hovered.
 */
/** The bound of a clipped cell: its pixel width, or none of its own for a share. */
const paloMaxWidth = (width: string | undefined): number | string =>
    undefined === width ? 210 : width.endsWith('%') ? 0 : width;

const titleWhenClipped = (event: { currentTarget: HTMLElement }): void => {
    const cell = event.currentTarget;

    if (cell.title) {
        return;
    }

    // The cell itself rarely runs over: most of them wrap their value in a
    // box of their own, and that is where the "…" appears. A pixel of slack,
    // because a sub-pixel layout reports a width that is a hair too small.
    const clipped =
        cell.scrollWidth > cell.clientWidth ||
        Array.from(cell.querySelectorAll('*')).some(child => child.scrollWidth > child.clientWidth + 1);

    if (clipped) {
        cell.title = cell.innerText.trim();
    }
};

export interface PaloColumn<T> {
    /** Stable key, used as the React key for header and body cells. */
    key: string;
    head: ReactNode;
    /**
     * What the column holds. It brings the width, the alignment and the
     * typography with it, so the same kind of value is the same width in
     * every list (LST-S1/S17, #2221). `align`, `variant` and `width` below
     * override it where a list has a reason.
     */
    type?: PaloColumnType;
    align?: 'left' | 'center' | 'right';
    /** Typographic treatment of the body cell (see PaloColumnVariant). */
    variant?: PaloColumnVariant;
    /** Sort field this column maps to; omit to make the column unsortable. */
    sortKey?: string;
    /**
     * Share of the table this column asks for, e.g. '40%' or '1%'. Without it
     * the browser splits the width by content, which lets a column of icons
     * with a long heading crowd out the column carrying the actual text.
     */
    width?: string;
    /**
     * Content that may take a second line instead of being cut, e.g. a row of
     * marks or a handful of chips. Text columns stay on one line.
     */
    wrap?: boolean;
    /**
     * The column that carries the answer — the recommended offer in a price
     * comparison. It is tinted over its whole height, header included.
     */
    highlighted?: boolean;
    /**
     * A filter for this column, shown in a row under the headings. The dialog
     * beside the list keeps the rest; this is the handful one reaches for
     * every day (#2209).
     */
    quickFilter?: PaloQuickFilter;
    /**
     * Worth showing where there is room, not worth scrolling sideways for: the
     * list drops these columns when it gets narrow. The column chooser still
     * lists them, and what the user turns off there stays off (#2194).
     */
    optional?: boolean;
    /**
     * Hidden until the user asks for it in the column chooser. For a column
     * that is right in principle but empty in practice — the rating on an
     * inquiry stands at "–" in 999 rows out of 1.000 (LST-S8/LST-S17).
     */
    hiddenByDefault?: boolean;
    /**
     * Carries the row's link where the list has one (see rowHref) — the
     * number, as a rule. Right-click there offers "open in new tab", which a
     * table row cannot. Without a column marked, the first one carries it.
     */
    link?: boolean;
    /**
     * The currency of the figures in this column. It puts the sign in the
     * heading ("Netto €"), where PaloAmount leaves the cells to the
     * figures alone (LST-S17).
     */
    currency?: string;
    cell: (item: T) => ReactNode;
}

/** Width under which a list drops the columns it can do without. */
const NARROW_LIST = 1250;

/** Marks the cells of such a column; the rule that hides them sits on the list. */
const OPTIONAL_COLUMN = 'palo-optional-column';

export interface PaloRowAction {
    label: ReactNode;
    onClick: () => void;
    /** Shown but not clickable — keeps an action visible where it exists but cannot be taken. */
    disabled?: boolean;
    /** Reason shown on hover, mainly to say why a disabled action is disabled. */
    hint?: ReactNode;
    /** Shown in the row itself while it is the row's only action (#2341). */
    icon?: ReactNode;
}

export interface PaloListPagination {
    count: number;
    /** 1-based page number. */
    page: number;
    limit: number;
    rowsPerPageOptions?: number[];
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    /** See PaloTableFoot; null for a list that is not read from the search engine. */
    maxResults?: number | null;
    /** See PaloTableFoot. */
    summary?: ReactNode;
}

export interface PaloListSelection<Id> {
    selectedIds: Id[];
    allSelected: boolean;
    someSelected: boolean;
    onToggle: (id: Id) => void;
    onToggleAll: () => void;
    /** Rows that cannot be picked — a declined offer, say. */
    disabledIds?: Id[];
    /** Why they cannot, shown when the reader points at the box. */
    disabledHint?: ReactNode;
}

export interface PaloListViewProps<T, Id extends number | string> {
    title?: ReactNode;
    /** Right-aligned header slot: primary buttons, a selection bar, etc. */
    headerActions?: ReactNode;
    /** Embed inside existing chrome: no canvas background, padding or title header. */
    embedded?: boolean;
    /** Optional block rendered between header and table (e.g. tabs). */
    tabs?: ReactNode;
    /**
     * Free-text search per the prototype's list toolbar. The input is debounced;
     * onSearchChange fires with the settled value (empty string clears).
     */
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    /** Offers exporting the list; omit to leave it out of the overflow menu. */
    onExport?: () => void;
    /**
     * Anything else the list can do, behind the dots next to the next step —
     * the place every mask keeps for what is not the one obvious action.
     */
    moreActions?: PaloOverflowAction[];
    /** Toolbar slot between search and export, e.g. the filter trigger. */
    filters?: ReactNode;
    /**
     * What the browser tab says while this list is open, already translated.
     *
     * The detail masks name themselves; the lists did not, so going back from
     * a record left its number standing in the tab over a list (#2212, R-22).
     */
    pageTitle?: string;
    /** Active-filter chips, shown under the toolbar. */
    filterChips?: ReactNode;
    /** Current sort, matched against a column's sortKey. */
    sort?: { key: string; order: 'asc' | 'desc' };
    /** Called with the column's sortKey and the order it should flip to. */
    onSortChange?: (key: string, order: 'asc' | 'desc') => void;
    columns: PaloColumn<T>[];
    /**
     * Keys of columns the user has hidden. A column without a key in here shows;
     * an unknown key is ignored, so a saved view that names a column which has
     * since been removed still opens.
     */
    hiddenColumnKeys?: string[];
    /**
     * Keys of columns the user asked to see although they are hidden by
     * default. Kept apart from the ones above: for these two kinds of column
     * the stored list means the opposite thing (#2221 R-136).
     */
    shownColumnKeys?: string[];
    /**
     * A filter control for one column, asked per column key.
     *
     * The list does not know which of its columns can be narrowed — the server
     * does, and says so. Returning nothing for a key leaves that column's cell
     * empty; returning nothing for every key leaves the row out altogether, so
     * a list that was never declared looks exactly as it did (#2209).
     */
    columnFilter?: (key: string) => ReactNode;
    /** Given, the toolbar offers the column chooser. */
    onToggleColumn?: (key: string, hiddenByDefault?: boolean) => void;
    /** Moves a column one place left or right; without it the arrows stay away. */
    onMoveColumn?: (key: string, direction: -1 | 1) => void;
    /**
     * Filters that narrow the list without being a column of it — a category,
     * a company. The bar stands above the table, so it is not bound to what
     * the table shows.
     */
    /**
     * Filters this list wires up itself, beside the ones its columns declare.
     *
     * Either a plain field — a key with options or free text — or a control
     * of the list's own, for a value no box can be typed: picking one
     * supplier out of 3.434 takes the autocomplete the masks use (#2239
     * R-139).
     */
    quickFilters?: { label: ReactNode; filter?: PaloQuickFilter; key?: string; control?: ReactNode }[];
    /**
     * A switch that belongs in the filter row rather than over a column, such
     * as "Gesperrte anzeigen" (LST-S14).
     */
    filterToggle?: ReactNode;
    /**
     * Shown in place of the empty label when the list has nothing to show and
     * there is still something to offer — "n gesperrte Treffer anzeigen".
     */
    emptyHint?: ReactNode;
    items: T[];
    getRowId: (item: T) => Id;
    requesting?: boolean;
    emptyLabel: ReactNode;
    onRowClick?: (item: T) => void;
    /**
     * Where a row leads. With it, Ctrl/⌘ or the middle button opens the row
     * in a new browser tab and the link column becomes a real link (GF
     * 27.10.2026, #2327); a plain click goes there in the same tab unless
     * onRowClick says otherwise.
     */
    rowHref?: (item: T) => string;
    /**
     * Rows that are still there but out of play — a locked employee, a closed
     * account. They stay legible and keep their menu, only muted.
     */
    isRowMuted?: (item: T) => boolean;
    /**
     * Rows that must stand out as a problem — a cancelled order a supplier must
     * not deliver (#2267). Drawn on the critical tint.
     */
    isRowCritical?: (item: T) => boolean;
    /** A notice between the header and the table, e.g. recent cancellations. */
    notice?: ReactNode;
    /**
     * The row whose details stand open beneath it, and what they show. The
     * screen holds which one is open, usually toggling it from onRowClick
     * (#2279).
     */
    expandedRowId?: Id;
    renderExpanded?: (item: T) => ReactNode;
    rowActions?: (item: T) => PaloRowAction[];
    selection?: PaloListSelection<Id>;
    /** Bulk actions shown in the selection bar above the table (prototype .sel-bar). */
    selectionActions?: ReactNode;
    pagination?: PaloListPagination;
    /** Extra nodes rendered inside the canvas, e.g. convert modals. */
    children?: ReactNode;
}

/** One empty list, so a refused list does not hand out a new one each render. */
const NO_ITEMS: never[] = [];

/**
 * Shared PALO list scaffold: canvas, header, optional tabs, dense table with
 * soft status pills, optional row selection, a three-dot row menu and
 * pagination. Content screens supply their columns and actions as config.
 */
/** The one thing in the bar that yields room to the others. */
const HEAD_SEARCH = 'palo-head-search';

export function PaloListView<T, Id extends number | string = number>(props: PaloListViewProps<T, Id>): ReactElement {
    const { getRowId, rowActions, selection, pagination, onSearchChange, rowHref } = props;
    const navigate = useNavigate();
    const opensRows = !!props.onRowClick || !!rowHref;

    const openRow = (item: T): void => {
        if (props.onRowClick) {
            props.onRowClick(item);
        } else if (rowHref) {
            navigate(rowHref(item));
        }
    };

    const clickRow = (event: MouseEvent, item: T): void => {
        if (rowHref && paloWantsNewTab(event)) {
            paloOpenInNewTab(rowHref(item));

            return;
        }

        openRow(item);
    };
    const shown = props.shownColumnKeys ?? [];
    const hidden = [
        ...(props.hiddenColumnKeys ?? []),
        // A column that is right in principle and empty in practice opens
        // switched off, and stays off until somebody asks for it in the
        // column chooser (LST-S8/LST-S17).
        ...props.columns.filter(column => column.hiddenByDefault && !shown.includes(column.key)).map(c => c.key),
    ];
    const [searchParams, setSearchParams] = useSearchParams();
    const refused = usePaloRefusedFilter();
    // Only a list that is filtered at all can be refused over its filter; the
    // tables inside a mask narrow nothing and are left alone.
    const isFiltered = undefined !== props.columnFilter || undefined !== props.quickFilters;
    const isRefused = isFiltered && null !== refused && refused === paloFilterPart(searchParams.toString());
    // Rows from before the refusal are not the answer to what was asked, and
    // showing them under chips that say the filter is applied is worse than
    // showing nothing (#2231 R-118).
    const items = isRefused ? NO_ITEMS : props.items;

    /** Takes back the filters this screen set, leaving the sub-view alone. */
    const discardFilters = (): void => {
        const next = new URLSearchParams(searchParams);

        Array.from(searchParams.keys())
            .filter(key => key.startsWith('filter[') && !key.endsWith('][]'))
            .forEach(key => next.delete(key));

        next.set('page', '1');
        setSearchParams(next);
    };

    useEffect(() => {
        if (props.pageTitle) {
            document.title = props.pageTitle;
        }
    }, [props.pageTitle]);

    // What the arrows in a detail mask walk through afterwards.
    useEffect(() => {
        if (items.length > 0) {
            rememberPaloListOrder(items.map(getRowId));
        }
    }, [items, getRowId]);
    const columns = props.columns.filter(column => !hidden.includes(column.key));
    const linkColumnKey = (columns.find(column => column.link) ?? columns[0])?.key;
    /*
     * The columns without a width of their own split what the fixed ones
     * leave over, in equal parts. Left to the browser the first of them takes
     * the lot: in the sales list the customer stood at its floor of 240 and
     * cut 4 names in 25, while the supplier beside it sat at 682 (GF
     * 13.10.2026).
     */
    const flexibleCount = columns.filter(paloColumnIsFlexible).length;
    const share = flexibleCount > 1 ? `${(100 / flexibleCount).toFixed(2)}%` : undefined;
    const { columnFilter } = props;
    // The declared filters stand in the same row as the hand-written quick
    // filters, above the table — not as a second heading row inside it: that
    // row was moved out once already, and a column's filter belongs where the
    // others are (#2209).
    const declaredFilters = useMemo(
        () =>
            columnFilter
                ? columns
                      .map(column => ({ key: column.key, label: column.head, cell: columnFilter(column.key) }))
                      .filter(entry => entry.cell)
                : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [columnFilter, columns.map(column => column.key).join('|')]
    );
    const [columnMenu, setColumnMenu] = useState<HTMLElement | null>(null);
    // Which row the keyboard is on. Kept apart from selection: moving through a
    // list is not the same as marking things in it.
    const [cursor, setCursor] = useState(-1);
    const bodyRef = useRef<HTMLTableSectionElement>(null);

    // A new page or a new filter is a new list; the old position means nothing.
    useEffect(() => setCursor(-1), [items]);

    // An address with a page beyond the backend's window — typed, bookmarked or
    // carried over from before the pager was capped — lands on the last page the
    // list can reach instead of on an error.
    const lastReachablePage =
        pagination && null !== pagination.maxResults
            ? Math.max(1, Math.floor((pagination.maxResults ?? PALO_MAX_RESULTS) / pagination.limit))
            : undefined;
    const pageBeyondWindow = undefined !== lastReachablePage && !!pagination && pagination.page > lastReachablePage;

    useEffect(() => {
        if (pageBeyondWindow && undefined !== lastReachablePage) {
            pagination?.onPageChange(lastReachablePage);
        }
        // Only the page decides; the callback is recreated on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageBeyondWindow, lastReachablePage]);

    const move = useCallback(
        (delta: number) => {
            if (0 === items.length) {
                return;
            }

            setCursor(current => {
                const from = current < 0 ? (delta > 0 ? -1 : 0) : current;

                return Math.min(items.length - 1, Math.max(0, from + delta));
            });
        },
        [items.length]
    );

    useEffect(() => {
        if (cursor < 0) {
            return;
        }

        bodyRef.current?.querySelector(`[data-row-index="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [cursor]);

    const atCursor = cursor >= 0 && cursor < items.length ? items[cursor] : undefined;

    usePaloShortcut({ combo: 'j', group: 'Liste', label: 'Nächster Eintrag', handler: () => move(1) });
    usePaloShortcut({ combo: 'k', group: 'Liste', label: 'Voriger Eintrag', handler: () => move(-1) });
    usePaloShortcut(
        opensRows && atCursor
            ? { combo: 'Enter', group: 'Liste', label: 'Eintrag öffnen', handler: () => openRow(atCursor) }
            : undefined
    );
    usePaloShortcut(
        selection && atCursor
            ? {
                  combo: 'x',
                  group: 'Liste',
                  label: 'Eintrag markieren',
                  handler: () => selection.onToggle(getRowId(atCursor)),
              }
            : undefined
    );
    const [menu, setMenu] = useState<{ anchor: HTMLElement; actions: PaloRowAction[] } | null>(null);

    // Debounced free-text search: type into the draft, settle into onSearchChange.
    const [searchDraft, setSearchDraft] = useState(props.searchValue ?? '');
    useEffect(() => {
        setSearchDraft(props.searchValue ?? '');
    }, [props.searchValue]);
    useEffect(() => {
        if (!onSearchChange || (props.searchValue ?? '') === searchDraft) {
            return undefined;
        }
        const timer = setTimeout(() => onSearchChange(searchDraft), 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDraft]);

    const colSpan = columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0);

    // Whether the list is narrowed by a search, which its empty state has to
    // say out loud: a term kept from another sitting otherwise reads as an
    // empty list (#2221). The chip for it sits with the other chips.
    const searched = Boolean(props.searchValue) && Boolean(onSearchChange);

    const { embedded } = props;
    const showHeader =
        !!props.title ||
        !!props.headerActions ||
        !!onSearchChange ||
        !!props.onExport ||
        !!props.filters ||
        !!props.onToggleColumn;
    // A list is a page like any other: it names itself in the top bar and
    // puts its toolbar there too, so the content starts with the table.
    const headSlot = usePaloHeadSlot(!embedded && showHeader);
    const inHeadSlot = !embedded && showHeader && null !== headSlot.element;
    const selectedCount = selection?.selectedIds.length ?? 0;
    const quickFilters: { label: ReactNode; filter?: PaloQuickFilter; key?: string; control?: ReactNode }[] = [
        ...props.columns
            .filter(column => column.quickFilter)
            .map(column => ({ label: column.head, filter: column.quickFilter as PaloQuickFilter })),
        ...(props.quickFilters ?? []),
    ];

    const searchField = onSearchChange ? (
        <TextField
            size="small"
            value={searchDraft}
            onChange={event => setSearchDraft(event.target.value)}
            placeholder={props.searchPlaceholder ?? 'Suchen …'}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchOutlined sx={{ fontSize: 15, color: tokens.color.ink3 }} />
                    </InputAdornment>
                ),
            }}
            // In the bar this is the widest thing and the only one that can
            // give up room. Fixed, it pushed the name of the list down to
            // nothing: at 891 pixels the demand list had no title left at all
            // (#2231 R-125).
            className={inHeadSlot ? HEAD_SEARCH : undefined}
            sx={inHeadSlot ? { width: 220, minWidth: 110, flex: '0 1 auto' } : { width: 240 }}
        />
    ) : null;

    const columnChooser = props.onToggleColumn ? (
        <>
            <Button
                size="small"
                startIcon={<ViewWeekOutlined sx={{ fontSize: 15 }} />}
                onClick={event => setColumnMenu(event.currentTarget)}
            >
                <FormattedMessage id="app.list.columns" />
            </Button>
            <Menu anchorEl={columnMenu} open={!!columnMenu} onClose={() => setColumnMenu(null)}>
                {props.columns.map((column, index) => (
                    <MenuItem
                        key={column.key}
                        onClick={() => props.onToggleColumn?.(column.key, column.hiddenByDefault)}
                    >
                        <Checkbox size="small" checked={!hidden.includes(column.key)} sx={{ p: 0, mr: 1 }} />
                        <Box sx={{ flex: 1, mr: 2 }}>{column.head}</Box>
                        {props.onMoveColumn && (
                            // The order is the same list, so it is set in the
                            // same place. One step at a time — dragging in a
                            // menu that closes on every click is a trap.
                            <Box sx={{ display: 'flex', gap: '2px' }}>
                                <IconButton
                                    size="small"
                                    disabled={0 === index}
                                    aria-label="Nach links"
                                    onClick={event => {
                                        event.stopPropagation();
                                        props.onMoveColumn?.(column.key, -1);
                                    }}
                                >
                                    <KeyboardArrowUp sx={{ fontSize: 15 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    disabled={index === props.columns.length - 1}
                                    aria-label="Nach rechts"
                                    onClick={event => {
                                        event.stopPropagation();
                                        props.onMoveColumn?.(column.key, 1);
                                    }}
                                >
                                    <KeyboardArrowDown sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Box>
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </>
    ) : null;

    // Exporting is not the next step on any list, so it sits with the rest
    // behind the dots instead of as a button of its own.
    const moreActions: PaloOverflowAction[] = [
        ...(props.onExport ? [{ label: <FormattedMessage id="app.common.export" />, onClick: props.onExport }] : []),
        ...(props.moreActions ?? []),
    ];

    /**
     * What the bar had to cut away, said on hovering.
     *
     * Read back off the screen rather than taken from the prop: a list names
     * itself with a message more often than with a plain string, and the
     * string check this started as therefore fired on almost none of them
     * (#2231 R-125).
     */
    const titleRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        titleRef.current?.setAttribute('title', titleRef.current.textContent ?? '');
    });

    const headRow = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: inHeadSlot ? '10px' : '16px',
                ...(inHeadSlot
                    ? { width: '100%', minWidth: 0, flexWrap: 'nowrap' }
                    : { mb: 3, flexWrap: 'wrap', rowGap: '12px' }),
            }}
        >
            <Box
                ref={titleRef}
                sx={
                    inHeadSlot
                        ? {
                              fontSize: '14px',
                              fontWeight: 640,
                              letterSpacing: '-.015em',
                              color: tokens.color.ink,
                              minWidth: 54,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                          }
                        : { flex: 1 }
                }
            >
                {inHeadSlot ? props.title : <Typography variant="h3">{props.title}</Typography>}
            </Box>
            <Box
                sx={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    minWidth: 0,
                    flexShrink: inHeadSlot ? 1 : 0,
                    // The search field is the only one of these that may give
                    // up room. Letting the group shrink as a whole took it out
                    // of the button beside it instead, which then read "eu:
                    // Bedarfserhebun" (#2231 R-125).
                    [`& > :not(.${HEAD_SEARCH})`]: { flexShrink: 0 },
                    flexWrap: inHeadSlot ? 'nowrap' : 'wrap',
                }}
            >
                {searchField}
                {props.filters}
                {columnChooser}
                <PaloOverflowMenu actions={moreActions} />
                {props.headerActions}
            </Box>
        </Box>
    );

    return (
        <PaloThemeProvider>
            {inHeadSlot && createPortal(headRow, headSlot.element as HTMLElement)}
            <Box
                sx={{
                    /*
                     * The list measures itself, so the columns that are worth
                     * showing but not worth scrolling sideways for can go when
                     * it gets narrow. Measured on the order list: with every
                     * column on, the table cannot be drawn under 1204 pixels,
                     * and a 1440 screen with the navigation open leaves 1108
                     * for it — so they go before the page scrolls (#2194).
                     */
                    containerType: 'inline-size',
                    [`@container (max-width: ${NARROW_LIST}px)`]: {
                        [`& .${OPTIONAL_COLUMN}`]: { display: 'none' },
                    },
                    backgroundColor: embedded ? 'transparent' : tokens.color.canvas,
                    p: embedded ? 0 : 4,
                    /*
                     * Standing on its own the list fills the area it was given
                     * and scrolls inside it, rather than growing and letting
                     * the shell scroll. That is what keeps the column header
                     * in view at 50 rows a page: a sticky header holds on to
                     * the nearest scrolling box, and that box has to be the
                     * one the rows move in (GF 12.10.2026, #2231/#2221).
                     */
                    ...(embedded ? {} : { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }),
                    // Embedded, the list lives inside a shell that hands out a
                    // fixed height and scrolls nothing itself, so the table has
                    // to bring its own scroller — otherwise everything past the
                    // visible rows, the pager included, is simply cut off.
                    ...(embedded && { flex: 1, display: 'flex', flexDirection: 'column' }),
                }}
            >
                {showHeader && !inHeadSlot && headRow}

                {/* A bar of its own, above the table and before the chips —
                    where ERPNext puts it and where #2209 says it belongs. Each
                    field carries the name of what it narrows, because a filter
                    bar does not line up with the columns underneath it. */}
                {quickFilters.length + declaredFilters.length > 0 || props.filterToggle ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            alignItems: 'flex-end',
                            mb: '10px',
                        }}
                    >
                        {[
                            // A field the list declares and this list also
                            // wrote a control for by hand stands in the row
                            // once, not twice — the order type stood there
                            // twice (#2231 R-120). The declared one stays,
                            // because it is the one the dialog and the chips
                            // speak the same language as: it names its
                            // comparison in the address, while a hand-written
                            // control writes the bare key, and the two would
                            // then be two filters on one field.
                            ...quickFilters
                                .map(entry => ({
                                    key: entry.filter?.key ?? entry.key ?? '',
                                    label: entry.label,
                                    cell: entry.filter ? <PaloQuickFilterCell filter={entry.filter} /> : entry.control,
                                }))
                                .filter(entry => !declaredFilters.some(field => field.key === entry.key)),
                            ...declaredFilters,
                        ].map(entry => (
                            <Box key={entry.key} sx={{ minWidth: 150 }}>
                                <Box
                                    sx={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        letterSpacing: '.06em',
                                        textTransform: 'uppercase',
                                        color: tokens.color.ink3,
                                        mb: '2px',
                                    }}
                                >
                                    {entry.label}
                                </Box>
                                {entry.cell}
                            </Box>
                        ))}
                        {props.filterToggle}
                    </Box>
                ) : null}

                {props.filterChips}
                {props.notice}

                {props.tabs}

                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: tokens.shadow.s1,
                        /*
                         * Wider than its box, the table used to be cut off
                         * rather than scrolled: at 947 pixels it wanted 925 and
                         * had 559, and the columns past that edge could not be
                         * reached at all (#2231 R-125). The column header no
                         * longer follows the page down in return — it now
                         * belongs to this scroller, which has the height of its
                         * own rows and never scrolls them.
                         */
                        overflow: embedded ? 'hidden' : 'auto',
                        flex: 1,
                        minHeight: 0,
                        ...(embedded && { display: 'flex', flexDirection: 'column' }),
                    }}
                >
                    {selection && selectedCount > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '7px 11px',
                                backgroundColor: tokens.color.accentSoft,
                                borderBottom: `1px solid ${tokens.color.accentLine}`,
                                fontSize: '12.5px',
                                color: tokens.color.accentHi,
                            }}
                        >
                            <Box component="b" sx={{ fontWeight: 640 }}>
                                {selectedCount} ausgewählt
                            </Box>
                            {props.selectionActions}
                        </Box>
                    )}
                    <Box sx={embedded ? { flex: 1, minHeight: 0, overflow: 'auto' } : undefined}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {selection && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selection.allSelected}
                                                indeterminate={selection.someSelected && !selection.allSelected}
                                                onChange={selection.onToggleAll}
                                            />
                                        </TableCell>
                                    )}
                                    {columns.map(column => {
                                        const sorted = !!column.sortKey && props.sort?.key === column.sortKey;
                                        const sortable = !!column.sortKey && !!props.onSortChange;

                                        return (
                                            <TableCell
                                                key={column.key}
                                                className={column.optional ? OPTIONAL_COLUMN : undefined}
                                                align={paloColumnLayout(column, share).align}
                                                width={paloColumnLayout(column, share).width}
                                                style={{
                                                    minWidth: paloColumnLayout(column, share).minWidth,
                                                    // A heading may wrap between words, never
                                                    // inside one: in a narrow embedded list
                                                    // "ALTER WERT" came out a letter per line.
                                                    wordBreak: 'normal',
                                                    overflowWrap: 'normal',
                                                    ...(column.highlighted
                                                        ? { backgroundColor: tokens.color.accentSoft }
                                                        : {}),
                                                }}
                                                onClick={
                                                    sortable
                                                        ? () =>
                                                              props.onSortChange?.(
                                                                  column.sortKey as string,
                                                                  sorted && props.sort?.order === 'asc' ? 'desc' : 'asc'
                                                              )
                                                        : undefined
                                                }
                                                sx={{
                                                    cursor: sortable ? 'pointer' : undefined,
                                                    color: sorted ? tokens.color.accent : undefined,
                                                    userSelect: sortable ? 'none' : undefined,
                                                }}
                                            >
                                                {column.head}
                                                {column.currency && ` ${paloCurrencySign(column.currency)}`}
                                                {sorted && (
                                                    <Box component="span" sx={{ fontSize: 8, ml: '3px' }}>
                                                        {props.sort?.order === 'asc' ? '▲' : '▼'}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                    {rowActions && <TableCell padding="checkbox" />}
                                </TableRow>
                            </TableHead>
                            <TableBody ref={bodyRef}>
                                {items.map((item, index) => {
                                    const id = getRowId(item);
                                    const selected = !!selection?.selectedIds.includes(id);
                                    const onCursor = index === cursor;
                                    const expanded = undefined !== props.expandedRowId && id === props.expandedRowId;

                                    return (
                                        <Fragment key={id}>
                                            <TableRow
                                                selected={selected || expanded}
                                                data-row-index={index}
                                                hover={opensRows}
                                                onClick={opensRows ? event => clickRow(event, item) : undefined}
                                                // The middle button: a new tab, as on any link. Pressed,
                                                // it would start the browser's scroll mode instead.
                                                onMouseDown={
                                                    rowHref
                                                        ? event => 1 === event.button && event.preventDefault()
                                                        : undefined
                                                }
                                                onAuxClick={
                                                    rowHref
                                                        ? event => 1 === event.button && paloOpenInNewTab(rowHref(item))
                                                        : undefined
                                                }
                                                sx={{
                                                    cursor: opensRows ? 'pointer' : 'default',
                                                    ...(props.isRowMuted?.(item) ? { opacity: 0.5 } : {}),
                                                    ...(props.isRowCritical?.(item)
                                                        ? { backgroundColor: tokens.color.critSoft }
                                                        : {}),
                                                    // Grey, not blue: a marked row is not an
                                                    // action (colour concept, #2290).
                                                    '&.MuiTableRow-hover:hover': {
                                                        backgroundColor: tokens.color.neutralSoft,
                                                    },
                                                    '&.Mui-selected, &.Mui-selected:hover': {
                                                        backgroundColor: tokens.color.neutralSoft,
                                                    },
                                                    '&.Mui-selected > td:first-of-type': {
                                                        boxShadow: `inset 2px 0 0 ${tokens.color.ink2}`,
                                                    },
                                                    // The keyboard cursor reads as an outline, so it stays
                                                    // apart from the filled look of a marked row.
                                                    ...(onCursor
                                                        ? {
                                                              outline: `1px solid ${tokens.color.accent}`,
                                                              outlineOffset: '-1px',
                                                          }
                                                        : {}),
                                                }}
                                            >
                                                {selection && (
                                                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                                        <Tooltip
                                                            title={
                                                                selection.disabledIds?.includes(id)
                                                                    ? selection.disabledHint ?? ''
                                                                    : ''
                                                            }
                                                            placement="top"
                                                        >
                                                            <span>
                                                                <Checkbox
                                                                    checked={selection.selectedIds.includes(id)}
                                                                    disabled={selection.disabledIds?.includes(id)}
                                                                    onChange={() => selection.onToggle(id)}
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                )}
                                                {columns.map(column => (
                                                    <TableCell
                                                        key={column.key}
                                                        className={column.optional ? OPTIONAL_COLUMN : undefined}
                                                        align={paloColumnLayout(column, share).align}
                                                        style={{ minWidth: paloColumnLayout(column, share).minWidth }}
                                                        onMouseEnter={column.wrap ? undefined : titleWhenClipped}
                                                        sx={{
                                                            ...(column.highlighted
                                                                ? { backgroundColor: tokens.color.accentSoft }
                                                                : {}),
                                                            // Prototype .tbl tbody td: clamp long content
                                                            ...(column.wrap
                                                                ? { whiteSpace: 'normal' }
                                                                : {
                                                                      whiteSpace: 'nowrap',
                                                                      overflow: 'hidden',
                                                                      textOverflow: 'ellipsis',
                                                                      // Its own column where the kind gives
                                                                      // one; 210 was the figure from before
                                                                      // the kinds carried widths. A share
                                                                      // in percent is no bound to a table
                                                                      // laid out by its content: one long
                                                                      // value widened the column to 2,248
                                                                      // pixels. Zero lets the share and the
                                                                      // floor decide, and the value clips.
                                                                      maxWidth: paloMaxWidth(
                                                                          paloColumnLayout(column, share).width
                                                                      ),
                                                                      // A cell that stacks a title over a
                                                                      // description clips inside its own
                                                                      // boxes, which cut words in half
                                                                      // without saying that anything is
                                                                      // missing.
                                                                      '& *': {
                                                                          overflow: 'hidden',
                                                                          textOverflow: 'ellipsis',
                                                                      },
                                                                  }),
                                                            ...(paloColumnLayout(column, share).variant
                                                                ? columnVariantSx[
                                                                      paloColumnLayout(column, share)
                                                                          .variant as PaloColumnVariant
                                                                  ]
                                                                : undefined),
                                                        }}
                                                    >
                                                        {rowHref && column.key === linkColumnKey ? (
                                                            <Box
                                                                {...paloLinkProps(rowHref(item))}
                                                                // The browser opens the link itself on a
                                                                // modified or middle click; the row must not
                                                                // open it a second time. A plain click goes
                                                                // on to the row, which knows how to open it.
                                                                onClick={(event: MouseEvent) => {
                                                                    if (paloWantsNewTab(event)) {
                                                                        event.stopPropagation();
                                                                    } else {
                                                                        event.preventDefault();
                                                                    }
                                                                }}
                                                                onAuxClick={(event: MouseEvent) =>
                                                                    event.stopPropagation()
                                                                }
                                                                sx={paloLinkReset}
                                                            >
                                                                {column.cell(item)}
                                                            </Box>
                                                        ) : (
                                                            column.cell(item)
                                                        )}
                                                    </TableCell>
                                                ))}
                                                {rowActions && (
                                                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                                        {/* A row with nothing to do keeps the column but not the
                                                        button: an empty menu is worse than no menu. */}
                                                        {paloSoleIconAction(rowActions(item)) ? (
                                                            <PaloRowActionButton
                                                                action={{
                                                                    ...rowActions(item)[0],
                                                                    reason: rowActions(item)[0].hint,
                                                                }}
                                                            />
                                                        ) : (
                                                            rowActions(item).length > 0 && (
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Weitere Aktionen"
                                                                    onClick={e =>
                                                                        setMenu({
                                                                            anchor: e.currentTarget,
                                                                            actions: rowActions(item),
                                                                        })
                                                                    }
                                                                >
                                                                    <MoreVert fontSize="small" />
                                                                </IconButton>
                                                            )
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                            {expanded && props.renderExpanded && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={colSpan}
                                                        sx={{
                                                            backgroundColor: tokens.color.surface2,
                                                            boxShadow: `inset 2px 0 0 ${tokens.color.accent}`,
                                                            whiteSpace: 'normal',
                                                            padding: '12px 16px',
                                                        }}
                                                    >
                                                        {props.renderExpanded(item)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    );
                                })}
                                {/* Loading a list that already shows rows leaves them
                                    standing — replacing them with bars makes the page
                                    jump for nothing. Only a list with nothing in it
                                    yet gets the skeleton (#2194). */}
                                {props.requesting &&
                                    0 === items.length &&
                                    Array.from({ length: 8 }).map((_, row) => (
                                        <TableRow key={`skeleton-${row}`}>
                                            {selection && <TableCell padding="checkbox" />}
                                            {columns.map(column => (
                                                <TableCell key={column.key}>
                                                    <Skeleton variant="text" width={`${55 + ((row * 7) % 35)}%`} />
                                                </TableCell>
                                            ))}
                                            {rowActions && <TableCell padding="checkbox" />}
                                        </TableRow>
                                    ))}
                                {isRefused && (
                                    <TableRow>
                                        <TableCell colSpan={colSpan}>
                                            <Typography variant="body2" sx={{ py: 2, color: tokens.color.crit }}>
                                                {/* Named here rather than in a notification that
                                                    slides away: the filter that caused it is still
                                                    standing right above (#2231 R-118). */}
                                                Mit diesem Filter lässt sich die Liste nicht laden.{' '}
                                                <Box
                                                    component="span"
                                                    role="button"
                                                    onClick={discardFilters}
                                                    sx={{
                                                        color: tokens.color.accent,
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                    }}
                                                >
                                                    Filter verwerfen
                                                </Box>
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!isRefused && !props.requesting && items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={colSpan}>
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                {/* A list keeps what it was last set to, so an empty
                                                    one is as often a search from another day as it
                                                    is an empty list (#2221). */}
                                                {searched ? (
                                                    <>
                                                        Keine Treffer für „{props.searchValue}“ —{' '}
                                                        <Box
                                                            component="span"
                                                            role="button"
                                                            onClick={() => onSearchChange?.('')}
                                                            sx={{
                                                                color: tokens.color.accent,
                                                                cursor: 'pointer',
                                                                textDecoration: 'underline',
                                                            }}
                                                        >
                                                            Suche zurücksetzen
                                                        </Box>
                                                    </>
                                                ) : (
                                                    props.emptyLabel
                                                )}
                                                {props.emptyHint && <Box sx={{ mt: '6px' }}>{props.emptyHint}</Box>}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* The foot belongs inside the table card, as in the prototype —
                        and outside the scroller, so it stays reachable while the
                        rows move under it. */}
                    {/* The count belongs to the answer before the refusal, and
                        read under an empty table it says the list is hiding
                        rows it has (#2231 R-118). */}
                    {pagination && !isRefused && (
                        <PaloTableFoot
                            count={pagination.count}
                            page={pagination.page}
                            limit={pagination.limit}
                            rowsPerPageOptions={pagination.rowsPerPageOptions}
                            onPageChange={pagination.onPageChange}
                            onLimitChange={pagination.onLimitChange}
                            maxResults={pagination.maxResults}
                            summary={pagination.summary}
                        />
                    )}
                </TableContainer>

                <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
                    {menu?.actions.map((action, index) => {
                        const item = (
                            <MenuItem
                                key={index}
                                disabled={action.disabled}
                                onClick={() => {
                                    action.onClick();
                                    setMenu(null);
                                }}
                            >
                                {action.label}
                            </MenuItem>
                        );

                        // A disabled MenuItem swallows the hover, so the tooltip
                        // needs a wrapper that still receives it.
                        return action.hint ? (
                            <Tooltip key={index} title={action.hint} placement="left">
                                <Box>{item}</Box>
                            </Tooltip>
                        ) : (
                            item
                        );
                    })}
                </Menu>

                {props.children}
            </Box>
        </PaloThemeProvider>
    );
}
